<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="960" scrollable>
    <v-card>
      <v-card-title class="dialog-title">
        <v-icon small color="#059669" class="mr-2">mdi-leaf</v-icon>
        {{ $t('org-dashboard.eco-drilldown-title') }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- KPI summary bar -->
        <div v-if="totals" class="drilldown-kpi-bar">
          <div class="drilldown-kpi">
            <span class="drilldown-kpi-value">{{ totals.users || 0 }}</span>
            <span class="drilldown-kpi-label">{{ $t('org-dashboard.active-users') }}</span>
          </div>
          <div class="drilldown-kpi">
            <span class="drilldown-kpi-value drilldown-kpi-value--green">{{ formatTons(totals.co2SavedKg) }}</span>
            <span class="drilldown-kpi-label">{{ $t('org-dashboard.eco-co2-saved') }}</span>
          </div>
          <div class="drilldown-kpi">
            <span class="drilldown-kpi-value">{{ totals.treesEquivalent || 0 }}</span>
            <span class="drilldown-kpi-label">{{ $t('org-dashboard.eco-trees-equiv') }}</span>
          </div>
        </div>

        <!-- Data table -->
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :server-items-length="totalItems"
          :options.sync="internalOptions"
          :loading="loading"
          :no-data-text="$t('org-dashboard.peak-no-data')"
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
          }"
          @update:options="onOptionsChange"
          class="drilldown-table"
        >
          <template v-slot:item.name="{ item }">
            <a class="drilldown-link" @click.stop="$emit('member-click', item.customerId)">{{ item.name }}</a>
            <span v-if="item.flag === 'high_usage'" class="flag-chip flag-chip--green">Top</span>
            <span v-else-if="item.flag === 'low_usage'" class="flag-chip flag-chip--orange">Low</span>
          </template>

          <template v-slot:item.co2SavedKg="{ item }">
            {{ formatKg(item.co2SavedKg) }}
          </template>

          <template v-slot:item.energy="{ item }">
            {{ formatEnergy(item.energy) }}
          </template>

          <template v-slot:item.estimatedKm="{ item }">
            {{ item.estimatedKm != null ? Math.round(item.estimatedKm).toLocaleString() : '-' }}
          </template>

          <template v-slot:item.treesEquivalent="{ item }">
            {{ item.treesEquivalent != null ? item.treesEquivalent : '-' }}
          </template>

          <template v-slot:item.sessions="{ item }">
            {{ item.sessions != null ? item.sessions : '-' }}
          </template>

          <template v-slot:item.lastSession="{ item }">
            {{ formatDate(item.lastSession) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'DashboardEcoDriversDrilldown',

  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    totals: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      internalOptions: { page: 1, itemsPerPage: 20, sortBy: ['co2SavedKg'], sortDesc: [true] },
    }
  },

  computed: {
    tableHeaders() {
      return [
        { text: this.$t('org-dashboard.eco-rank'), value: 'rank', sortable: false, width: 50 },
        { text: this.$t('org-dashboard.eco-driver'), value: 'name' },
        { text: 'CO₂', value: 'co2SavedKg' },
        { text: 'kWh', value: 'energy' },
        { text: 'Km', value: 'estimatedKm', sortable: false },
        { text: this.$t('org-dashboard.eco-trees-equiv'), value: 'treesEquivalent', sortable: false },
        { text: 'Sessions', value: 'sessions' },
        { text: this.$t('org-dashboard.eco-drilldown-last-session'), value: 'lastSession', sortable: false },
      ]
    },
  },

  methods: {
    onOptionsChange(options) {
      this.internalOptions = options
      this.$emit('update:options', options)
    },

    formatTons(kg) {
      if (kg == null) return '-'
      const tons = kg / 1000
      if (tons >= 1000) return Math.round(tons).toLocaleString() + ' t'
      if (tons >= 1) return tons.toFixed(1) + ' t'
      return Math.round(kg) + ' kg'
    },

    formatKg(val) {
      if (val == null) return '-'
      const tons = val / 1000
      if (tons >= 1) return tons.toFixed(2) + ' t'
      return Math.round(val) + ' kg'
    },

    formatEnergy(val) {
      if (val == null) return '-'
      if (val >= 1000) return (val / 1000).toFixed(1) + ' MWh'
      return Math.round(val) + ' kWh'
    },

    formatDate(val) {
      if (!val) return '-'
      return dayjs(val).format('DD/MM/YYYY HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-kpi-bar {
  display: flex;
  gap: 24px;
  padding: 16px 24px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-kpi {
  display: flex;
  flex-direction: column;
}

.drilldown-kpi-value {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;

  &--green {
    color: #059669;
  }
}

.drilldown-kpi-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.drilldown-table {
  ::v-deep .v-data-table__wrapper {
    max-height: 500px;
    overflow-y: auto;
  }
}

.drilldown-link {
  color: #FF5722;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration-thickness: 2px;
  }
}

.flag-chip {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  border-radius: 4px;
  padding: 1px 4px;
  margin-left: 4px;
  vertical-align: middle;

  &--green {
    color: #059669;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  &--orange {
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fde68a;
  }
}
</style>
